import { useAuth0 } from "@auth0/auth0-react";
import { DialogClose } from "@radix-ui/react-dialog";
import {
  ApiError,
  MessageType,
  FormSummary,
  MessagesService,
  OrgMessageRecipient,
  ParentLimitedResponse,
  StudentLimitedResponse,
  MeetingTutorLimitedResponse,
  TutorLimitedResponse,
  MeetingStudentLimitedResponse,
  MessageDirection,
} from "client/openapi";
import { ButtonColor } from "components/Button";
import { DialogAction, DialogActions, DialogContent } from "components/Dialog";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Select } from "components/Select";
import { APIResponse, PageStatus } from "types";
import { concatenateName } from "util/concatenateName";
import Notifications from "util/notifications";
import "./index.css";
import SuccessDialog from "components/SuccessDialog";
import TextInput from "components/Inputs/TextInput";
import {
  FormPicker,
  noFormsText,
  useForms,
} from "components/Forms/SendFormControls";
import {
  errorText,
  sendFormInBatches,
  sendSummary,
} from "components/Forms/formsApi";
import {
  Auth0AccountRole,
  FormRecipient,
  MessageTemplateSummary,
  TemplateRecipient,
} from "client/openapi";
import {
  TemplatePicker,
  useTemplates,
  versionsOf,
} from "components/MessageTemplates/SendTemplateControls";
import TemplateMessage from "components/MessageTemplates/TemplateMessage";
import {
  TemplateWording,
  sendTemplateInBatches,
  sendSummary as templateSendSummary,
} from "components/MessageTemplates/templatesApi";
import { PeopleTab } from "client/openapi/models/PeopleTab";
import { PersonKind } from "client/openapi/models/PersonKind";
import { PersonName } from "client/openapi/models/PersonName";
import { PeopleService } from "client/openapi/services/PeopleService";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import PeoplePicker, {
  TaskPerson,
} from "components/Dashboard/ManageTasksDialog/PeoplePicker";

type MessageDraft = {
  students:
    | (StudentLimitedResponse | MeetingStudentLimitedResponse)[]
    | undefined;
  tutors: (TutorLimitedResponse | MeetingTutorLimitedResponse)[] | undefined;
  parents: ParentLimitedResponse[] | undefined;
  subject: string;
  content: string;
};

/**
 * "Send a form (optional)" for an admin. It lives inside the dialog's
 * content, which mounts only while the dialog is open, so the forms list is
 * fetched on opening (and fresh each time), never by a closed dialog.
 */
function MessageFormPicker({
  orgId,
  audience,
  hasTutors,
  formId,
  onChange,
  disabled,
}: {
  /** Null for someone who isn't an admin: no picker. */
  orgId: number | null;
  audience: FormSummary.audience;
  hasTutors: boolean;
  formId: number | null;
  onChange: (formId: number | null) => void;
  disabled: boolean;
}) {
  const forms = useForms(orgId, audience);
  if (forms === undefined) return null;
  return (
    <div className="mb-3">
      <span className="input-label font-bold">Send a form (optional)</span>
      <FormPicker
        forms={forms}
        formId={formId}
        onChange={onChange}
        placeholder="No form selected"
        disabled={disabled}
        emptyText={noFormsText(audience)}
      />
      {formId !== null && (
        <p className="mt-1 text-xs text-gray-600">
          {audience === FormSummary.audience.TUTORS
            ? "Each tutor gets their own link to the form."
            : hasTutors
            ? "Each student and parent gets their own link to the form. Tutors aren't sent anything."
            : "Each person gets their own link to the form."}
        </p>
      )}
    </div>
  );
}

/**
 * "Use a message template (optional)" for an admin. Like the form picker,
 * it lives inside the dialog's content, so the templates load on opening.
 * A template chosen before they loaded (a template's Send on the templates
 * list) is handed back once, with its versions, when they arrive.
 */
function MessageTemplatePicker({
  orgId,
  templateId,
  onChange,
  disabled,
}: {
  /** Null for someone who isn't an admin: no picker. */
  orgId: number | null;
  templateId: number | null;
  onChange: (template: MessageTemplateSummary | null) => void;
  disabled: boolean;
}) {
  const templates = useTemplates(orgId);
  const handedBack = useRef(false);
  useEffect(() => {
    if (!templates || handedBack.current || templateId === null) return;
    handedBack.current = true;
    onChange(templates.find((t) => t.id === templateId) ?? null);
  }, [templates, templateId, onChange]);
  if (templates === undefined) return null;
  return (
    <div className="mb-3">
      <span className="input-label font-bold">
        Use a message template (optional)
      </span>
      <TemplatePicker
        templates={templates}
        templateId={templateId}
        onChange={(id) => onChange(templates.find((t) => t.id === id) ?? null)}
        placeholder="No template selected"
        disabled={disabled}
      />
    </div>
  );
}

/**
 * Who the lookup offers: tutors for a tutors form, families for a families
 * form, and everyone for a message template.
 */
function lookupRoles(lookup: FormSummary.audience | "everyone"): PersonKind[] {
  if (lookup === "everyone")
    return [PersonKind.PARENT, PersonKind.STUDENT, PersonKind.TUTOR];
  return lookup === FormSummary.audience.TUTORS
    ? [PersonKind.TUTOR]
    : [PersonKind.PARENT, PersonKind.STUDENT];
}

const fullName = (person: PersonName) =>
  `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim();

/**
 * The organization's people (the People panel's All tab: archived people
 * are not on it) for the lookup, loaded when the dialog opens.
 */
function useLookupPeople(orgId: number, on: boolean) {
  const [people, setPeople] = useState<Record<PersonKind, PersonName[]>>();
  useEffect(() => {
    if (!on) return;
    PeopleService.getPeopleRecipients({
      orgId,
      requestBody: { tab: PeopleTab.ALL },
    })
      .then((everyone) =>
        setPeople({
          [PersonKind.PARENT]: everyone.parents,
          [PersonKind.STUDENT]: everyone.students,
          [PersonKind.TUTOR]: everyone.tutors,
        })
      )
      .catch((e) =>
        Notifications.error(errorText(e, "People could not be loaded."))
      );
  }, [orgId, on]);
  return people;
}

function ComposeMessageDialog({
  students,
  tutors,
  parents,
  messageAllStudents,
  messageAllTutors,
  messageAllParents,
  org_id,
  preloadedContent,
  initialFormId,
  initialTemplateId,
  lookup,
}: {
  students?: (StudentLimitedResponse | MeetingStudentLimitedResponse)[];
  tutors?: (TutorLimitedResponse | MeetingTutorLimitedResponse)[];
  parents?: ParentLimitedResponse[];
  messageAllStudents?: boolean;
  messageAllTutors?: boolean;
  messageAllParents?: boolean;
  org_id: number;
  preloadedContent?: string;
  /** A form already chosen in "Send a form" (a form's Send on the forms list). */
  initialFormId?: number;
  /** A message template already chosen (a template's Send on its list). */
  initialTemplateId?: number;
  /**
   * Pick who it goes to by looking people up: parents and students for a
   * families form, tutors for a tutors form, anyone for a template.
   */
  lookup?: FormSummary.audience | "everyone";
}) {
  const { user } = useAuth0();
  const { currently_selected_role } = useContext(OrgRolesAndAccountContext);

  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [sendSMS, setSendSMS] = useState<boolean>(true);

  const emptyDraft: MessageDraft = {
    subject: "",
    content: preloadedContent || "",
    students: students ? students : undefined,
    tutors: tutors ? tutors : undefined,
    parents: parents ? parents : undefined,
  };

  const [newMessage, setNewMessage] = useState<MessageDraft>(emptyDraft);
  // An admin can send a form instead of a plain message. A message to tutors
  // only offers tutors forms; otherwise families forms.
  const hasTutors = !!(messageAllTutors || tutors?.length);
  const hasFamilies = !!(
    messageAllStudents ||
    messageAllParents ||
    students?.length ||
    parents?.length
  );
  const audience =
    (lookup !== "everyone" ? lookup : undefined) ??
    (hasTutors && !hasFamilies
      ? FormSummary.audience.TUTORS
      : FormSummary.audience.FAMILIES);
  const [formId, setFormId] = useState<number | null>(initialFormId ?? null);
  // Or an admin sends a message template, filled in for each person.
  const [templateId, setTemplateId] = useState<number | null>(
    initialTemplateId ?? null
  );
  const [template, setTemplate] = useState<MessageTemplateSummary | null>(null);
  // The template's wording changed for this message only; none: its own.
  const [templateWording, setTemplateWording] = useState<TemplateWording>();
  const lookupPeople = useLookupPeople(org_id, lookup !== undefined);
  const [picked, setPicked] = useState<TaskPerson[]>([]);
  const [successText, setSuccessText] = useState("Message Sent!");
  const [status, setStatus] = useState<PageStatus>();
  const [error, setError] = useState<APIResponse>();

  // Check if recipients are empty *when not* messaging entire org/tutors/parents/students
  // (i.e., for createMessageToRecipients).
  // This ensures we disable the button if we'd produce an empty array.
  const recipientsAreEmpty =
    !(messageAllStudents || messageAllTutors || messageAllParents) &&
    !(
      (newMessage.students && newMessage.students.length > 0) ||
      (newMessage.tutors && newMessage.tutors.length > 0) ||
      (newMessage.parents && newMessage.parents.length > 0)
    );

  /**
   * The people the form is for: the students and parents of a families
   * form, the tutors of a tutors form. "Message all" of a kind means the
   * People panel's list of that kind (archived people are not on it).
   */
  async function formRecipients(): Promise<FormRecipient[]> {
    const everyone = async (tab: PeopleTab) =>
      PeopleService.getPeopleRecipients({
        orgId: org_id,
        requestBody: { tab },
      });
    const ids = {
      students: (newMessage.students || []).map((s) =>
        "student" in s ? s.student.id : s.id
      ),
      parents: (newMessage.parents || []).map((p) => p.id),
      tutors: (newMessage.tutors || []).map((t) =>
        "tutor" in t ? t.tutor.id : t.id
      ),
    };
    const families = audience === FormSummary.audience.FAMILIES;
    if (families && messageAllStudents) {
      ids.students = (await everyone(PeopleTab.STUDENT)).students.map(
        (p) => p.id
      );
    }
    if (families && messageAllParents) {
      ids.parents = (await everyone(PeopleTab.PARENT)).parents.map((p) => p.id);
    }
    if (!families && messageAllTutors) {
      ids.tutors = (await everyone(PeopleTab.TUTOR)).tutors.map((p) => p.id);
    }
    const people = (personType: Auth0AccountRole, list: number[]) =>
      list.map((id) => ({ person_type: personType, person_id: id }));
    return families
      ? [
          ...people(Auth0AccountRole.ME, ids.students),
          ...people(Auth0AccountRole.PARENT, ids.parents),
        ]
      : people(Auth0AccountRole.ORG_TUTOR, ids.tutors);
  }

  /** Each person gets their own link to the chosen form. */
  function sendForm(chosenFormId: number) {
    setStatus(PageStatus.LOADING);
    formRecipients()
      .then((recipients) =>
        sendFormInBatches({
          orgId: org_id,
          formId: chosenFormId,
          recipients,
          sms: sendSMS,
          email: sendEmail,
          content: newMessage.content,
          subject: newMessage.subject,
        })
      )
      .then((results) => {
        if (results.length === 0) {
          setStatus(PageStatus.ERROR);
          setError({ message: "Nobody here gets this form." });
          return;
        }
        const summary = sendSummary(results);
        if (results.every((r) => r.status !== "sent")) {
          setStatus(PageStatus.ERROR);
          setError({ message: summary }); // stay open to try another form or channel
          return;
        }
        setSuccessText(summary);
        setStatus(PageStatus.SUCCESS);
      })
      .catch((e) => {
        setStatus(PageStatus.ERROR);
        setError({ message: errorText(e, "The form could not be sent.") });
      });
  }

  /**
   * The people the template is for, of every kind chosen. "Message all" of
   * a kind means the People panel's list of that kind.
   */
  async function templateRecipients(): Promise<TemplateRecipient[]> {
    const everyone = async (tab: PeopleTab) =>
      PeopleService.getPeopleRecipients({
        orgId: org_id,
        requestBody: { tab },
      });
    const ids = {
      students: (newMessage.students || []).map((s) =>
        "student" in s ? s.student.id : s.id
      ),
      parents: (newMessage.parents || []).map((p) => p.id),
      tutors: (newMessage.tutors || []).map((t) =>
        "tutor" in t ? t.tutor.id : t.id
      ),
    };
    if (messageAllStudents) {
      ids.students = (await everyone(PeopleTab.STUDENT)).students.map(
        (p) => p.id
      );
    }
    if (messageAllParents) {
      ids.parents = (await everyone(PeopleTab.PARENT)).parents.map((p) => p.id);
    }
    if (messageAllTutors) {
      ids.tutors = (await everyone(PeopleTab.TUTOR)).tutors.map((p) => p.id);
    }
    const people = (personType: Auth0AccountRole, list: number[]) =>
      list.map((id) => ({ person_type: personType, person_id: id }));
    return [
      ...people(Auth0AccountRole.ME, ids.students),
      ...people(Auth0AccountRole.PARENT, ids.parents),
      ...people(Auth0AccountRole.ORG_TUTOR, ids.tutors),
    ];
  }

  /** Each person gets the template filled in with their own details. */
  function sendTemplate(chosenTemplateId: number) {
    setStatus(PageStatus.LOADING);
    templateRecipients()
      .then((recipients) =>
        sendTemplateInBatches({
          templateId: chosenTemplateId,
          recipients,
          sms: sendSMS,
          email: sendEmail,
          wording: templateWording,
        })
      )
      .then((results) => {
        if (results.length === 0) {
          setStatus(PageStatus.ERROR);
          setError({ message: "Nobody here gets this message." });
          return;
        }
        const summary = templateSendSummary(results);
        if (results.every((r) => r.status !== "sent")) {
          setStatus(PageStatus.ERROR);
          setError({ message: summary }); // stay open to try another way
          return;
        }
        setSuccessText(summary);
        setStatus(PageStatus.SUCCESS);
      })
      .catch((e) => {
        setStatus(PageStatus.ERROR);
        setError({ message: errorText(e, "The message could not be sent.") });
      });
  }

  /** A template picked (or cleared): it goes by the versions it has. */
  const chooseTemplate = useCallback(
    (picked: MessageTemplateSummary | null) => {
      setTemplate(picked);
      setTemplateId(picked?.id ?? null);
      setTemplateWording(undefined);
      if (picked) {
        setFormId(null);
        const versions = versionsOf(picked);
        setSendSMS(versions.sms);
        setSendEmail(versions.email);
      }
    },
    []
  );

  // The people already chosen, for the template's preview (the first) and
  // its blank details (the rest). "Message all" is looked up only on send.
  const listedRecipients = useMemo<TemplateRecipient[]>(
    () => [
      ...(newMessage.students || []).map((s) => ({
        person_type: Auth0AccountRole.ME,
        person_id: "student" in s ? s.student.id : s.id,
      })),
      ...(newMessage.parents || []).map((p) => ({
        person_type: Auth0AccountRole.PARENT,
        person_id: p.id,
      })),
      ...(newMessage.tutors || []).map((t) => ({
        person_type: Auth0AccountRole.ORG_TUTOR,
        person_id: "tutor" in t ? t.tutor.id : t.id,
      })),
    ],
    [newMessage.students, newMessage.parents, newMessage.tutors]
  );

  async function createMessage() {
    if (!newMessage || !user?.sub) {
      return;
    }

    if (templateId !== null) {
      sendTemplate(templateId);
      return;
    }

    if (formId !== null) {
      sendForm(formId);
      return;
    }

    setStatus(PageStatus.LOADING);

    if (messageAllStudents && messageAllTutors && messageAllParents) {
      MessagesService.createMessageToOrg({
        requestBody: {
          m_type: MessageType.DIRECT,
          subject: newMessage.subject,
          content: newMessage.content,
          sender_id: user?.sub,
          email: sendEmail,
          sms: sendSMS,
          org_id: org_id,
          direction: MessageDirection.OUTBOUND,
        },
      })
        .then(() => {
          setStatus(PageStatus.SUCCESS);
        })
        .catch((e: ApiError) => {
          setStatus(PageStatus.ERROR);
          setError({ error: "Unable to send message." });
          console.error(`Error (#${e.status}): ${e.message}`);
        });
    } else if (messageAllStudents) {
      MessagesService.createMessageToOrgStudents({
        requestBody: {
          m_type: MessageType.DIRECT,
          subject: newMessage.subject,
          content: newMessage.content,
          sender_id: user?.sub,
          email: sendEmail,
          sms: sendSMS,
          org_id: org_id,
          direction: MessageDirection.OUTBOUND,
        },
      })
        .then(() => {
          setStatus(PageStatus.SUCCESS);
        })
        .catch((e: ApiError) => {
          setStatus(PageStatus.ERROR);
          setError({ error: "Unable to send message." });
          console.error(`Error (#${e.status}): ${e.message}`);
        });
    } else if (messageAllTutors) {
      MessagesService.createMessageToOrgTutors({
        requestBody: {
          m_type: MessageType.DIRECT,
          subject: newMessage.subject,
          content: newMessage.content,
          sender_id: user?.sub,
          email: sendEmail,
          sms: sendSMS,
          org_id: org_id,
          direction: MessageDirection.OUTBOUND,
        },
      })
        .then(() => {
          setStatus(PageStatus.SUCCESS);
        })
        .catch((e: ApiError) => {
          setStatus(PageStatus.ERROR);
          setError({ error: "Unable to send message." });
          console.error(`Error (#${e.status}): ${e.message}`);
        });
    } else if (messageAllParents) {
      MessagesService.createMessageToOrgParents({
        requestBody: {
          m_type: MessageType.DIRECT,
          subject: newMessage.subject,
          content: newMessage.content,
          sender_id: user?.sub,
          email: sendEmail,
          sms: sendSMS,
          org_id: org_id,
          direction: MessageDirection.OUTBOUND,
        },
      })
        .then(() => {
          setStatus(PageStatus.SUCCESS);
        })
        .catch((e: ApiError) => {
          setStatus(PageStatus.ERROR);
          setError({ error: "Unable to send message." });
          console.error(`Error (#${e.status}): ${e.message}`);
        });
    } else if (
      lookup !== undefined ||
      (students && students.length > 0) ||
      (tutors && tutors.length > 0) ||
      (parents && parents.length > 0)
    ) {
      let studentIds: number[] = [];
      let tutorIds: number[] = [];
      let parentIds: number[] = [];

      newMessage.students &&
        newMessage.students.forEach((s) => {
          studentIds.push("student" in s ? s.student.id : s.id);
        });

      newMessage.tutors &&
        newMessage.tutors.forEach((t) => {
          tutorIds.push("tutor" in t ? t.tutor.id : t.id);
        });

      newMessage.parents &&
        newMessage.parents.forEach((p) => {
          parentIds.push(p.id);
        });

      MessagesService.createMessageToRecipients({
        requestBody: {
          m_type: MessageType.DIRECT,
          subject: newMessage.subject,
          content: newMessage.content,
          sender_id: user?.sub,
          org_id: org_id,
          email: sendEmail,
          sms: sendSMS,
          // An email to one person goes from the admin's own confirmed address.
          from_personal:
            studentIds.length + tutorIds.length + parentIds.length === 1,
          recipient_types:
            newMessage.students &&
            newMessage.students.length > 0 &&
            newMessage.tutors &&
            newMessage.tutors.length > 0 &&
            newMessage.parents &&
            newMessage.parents.length > 0
              ? [
                  OrgMessageRecipient.STUDENTS,
                  OrgMessageRecipient.TUTORS,
                  OrgMessageRecipient.PARENTS,
                ]
              : newMessage.students &&
                newMessage.students.length > 0 &&
                newMessage.tutors &&
                newMessage.tutors.length > 0
              ? [OrgMessageRecipient.STUDENTS, OrgMessageRecipient.TUTORS]
              : newMessage.students &&
                newMessage.students.length > 0 &&
                newMessage.parents &&
                newMessage.parents.length > 0
              ? [OrgMessageRecipient.STUDENTS, OrgMessageRecipient.PARENTS]
              : newMessage.tutors &&
                newMessage.tutors.length > 0 &&
                newMessage.parents &&
                newMessage.parents.length > 0
              ? [OrgMessageRecipient.TUTORS, OrgMessageRecipient.PARENTS]
              : newMessage.students && newMessage.students.length > 0
              ? [OrgMessageRecipient.STUDENTS]
              : newMessage.tutors && newMessage.tutors.length > 0
              ? [OrgMessageRecipient.TUTORS]
              : newMessage.parents && newMessage.parents.length > 0
              ? [OrgMessageRecipient.PARENTS]
              : [],
          student_recipients: studentIds.length > 0 ? studentIds : undefined,
          tutor_recipients: tutorIds.length > 0 ? tutorIds : undefined,
          parent_recipients: parentIds.length > 0 ? parentIds : undefined,
          direction: MessageDirection.OUTBOUND,
        },
      })
        .then(() => {
          setStatus(PageStatus.SUCCESS);
        })
        .catch((e: ApiError) => {
          setStatus(PageStatus.ERROR);
          setError({ error: "Unable to send message." });
          console.error(`Error (#${e.status}): ${e.message}`);
        });
    }
  }

  function handleUpdate(
    ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const value = ev.target.value;
    setNewMessage({
      ...newMessage,
      [ev.target.name]: value,
    });
  }

  function handleUpdateStudentRecipients(options: any) {
    setNewMessage({
      ...newMessage,
      students: options,
    });
  }

  function handleUpdateTutorRecipients(options: any) {
    setNewMessage({
      ...newMessage,
      tutors: options,
    });
  }

  function handleUpdateParentRecipients(options: any) {
    setNewMessage({
      ...newMessage,
      parents: options,
    });
  }

  /** The people looked up become the message's recipients. */
  function pick(people: TaskPerson[]) {
    setPicked(people);
    const chosen = (kind: PersonKind) =>
      (lookupPeople?.[kind] ?? []).filter((person) =>
        people.some((p) => p.role === kind && p.id === person.id)
      );
    setNewMessage({
      ...newMessage,
      students: chosen(PersonKind.STUDENT) as StudentLimitedResponse[],
      parents: chosen(PersonKind.PARENT) as ParentLimitedResponse[],
      tutors: chosen(PersonKind.TUTOR) as TutorLimitedResponse[],
    });
  }

  function handleSubmit() {
    return async (ev: React.FormEvent) => {
      ev.preventDefault();
      await createMessage();
    };
  }

  function clearForm() {
    setNewMessage(emptyDraft);
    setStatus(undefined);
    setSendEmail(false);
    setSendSMS(true);
    setFormId(initialFormId ?? null);
    // A template given at opening stays chosen, with its ways to send.
    chooseTemplate(
      template && template.id === initialTemplateId ? template : null
    );
    setPicked([]);
    setSuccessText("Message Sent!");
  }

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  if (status === PageStatus.SUCCESS) {
    return <SuccessDialog message={successText} onClose={clearForm} />;
  }

  const disableSendButton =
    (!sendEmail && !sendSMS) ||
    recipientsAreEmpty ||
    status === PageStatus.LOADING;

  return (
    <>
      <DialogContent className="dialog-content">
        <div className="invite-dialog">
          {messageAllStudents && messageAllTutors && messageAllParents ? (
            <h2 className="compose-msg-dialog--title">Message Organization</h2>
          ) : (students && tutors) ||
            (students && parents) ||
            (parents && tutors) ? (
            <h2 className="compose-msg-dialog--title">Message Attendees</h2>
          ) : messageAllStudents || students ? (
            <h2 className="compose-msg-dialog--title">Message Students</h2>
          ) : messageAllTutors || tutors ? (
            <h2 className="compose-msg-dialog--title">Message Tutors</h2>
          ) : messageAllParents || parents ? (
            <h2 className="compose-msg-dialog--title">Message Parents</h2>
          ) : (
            <h2 className="compose-msg-dialog--title">Send a Message</h2>
          )}

          <form onSubmit={handleSubmit()}>
            {/* STUDENT SELECT */}
            {students &&
              !(
                messageAllStudents ||
                messageAllTutors ||
                messageAllParents
              ) && (
                <div className="mb-3">
                  <label
                    htmlFor="student-recipients"
                    className="input-label font-bold"
                  >
                    Students
                  </label>
                  <Select
                    id="student-recipients"
                    name="student-recipients"
                    options={students}
                    defaultValue={students}
                    getOptionLabel={(s) => `${concatenateName(s)}`}
                    getOptionValue={(s) => s.id.toString()}
                    onChange={handleUpdateStudentRecipients}
                    isMulti
                  />
                </div>
              )}

            {/* TUTOR SELECT */}
            {tutors &&
              !(
                messageAllStudents ||
                messageAllTutors ||
                messageAllParents
              ) && (
                <div className="mb-3">
                  <label
                    htmlFor="tutor-recipients"
                    className="input-label font-bold"
                  >
                    Tutors
                  </label>
                  <Select
                    id="tutor-recipients"
                    name="tutor-recipients"
                    options={tutors}
                    defaultValue={tutors}
                    getOptionLabel={(t) => `${concatenateName(t)}`}
                    getOptionValue={(t) => t.id.toString()}
                    onChange={handleUpdateTutorRecipients}
                    isMulti
                  />
                </div>
              )}

            {/* PARENT SELECT */}
            {parents &&
              !(
                messageAllStudents ||
                messageAllTutors ||
                messageAllParents
              ) && (
                <div className="mb-3">
                  <label
                    htmlFor="parent-recipients"
                    className="input-label font-bold"
                  >
                    Parents
                  </label>
                  <Select
                    id="parent-recipients"
                    name="parent-recipients"
                    options={parents}
                    defaultValue={parents}
                    getOptionLabel={(p) => `${concatenateName(p)}`}
                    getOptionValue={(p) => p.id.toString()}
                    onChange={handleUpdateParentRecipients}
                    isMulti
                  />
                </div>
              )}

            {/* PEOPLE LOOKUP (a form's Send on the forms list) */}
            {lookup !== undefined && (
              <div className="mb-3">
                <label
                  htmlFor="message-lookup"
                  className="input-label font-bold"
                >
                  To
                </label>
                <PeoplePicker
                  inputId="message-lookup"
                  people={Object.entries(lookupPeople ?? {}).flatMap(
                    ([kind, people]) =>
                      people.map((person) => ({
                        role: kind as PersonKind,
                        id: person.id,
                        label: fullName(person),
                      }))
                  )}
                  roles={lookupRoles(lookup)}
                  value={picked}
                  onChange={pick}
                  placeholder={
                    lookupPeople ? "Search by name" : "Loading people..."
                  }
                  autoFocus
                />
              </div>
            )}

            {/* A FORM TO SEND (admins) */}
            <MessageFormPicker
              orgId={
                currently_selected_role === Auth0AccountRole.ORG_ADMIN
                  ? org_id
                  : null
              }
              audience={audience}
              hasTutors={hasTutors}
              formId={formId}
              onChange={(id) => {
                setFormId(id);
                if (id !== null) chooseTemplate(null);
              }}
              disabled={status === PageStatus.LOADING}
            />

            {/* OR A MESSAGE TEMPLATE (admins) */}
            <MessageTemplatePicker
              orgId={
                currently_selected_role === Auth0AccountRole.ORG_ADMIN
                  ? org_id
                  : null
              }
              templateId={templateId}
              onChange={chooseTemplate}
              disabled={status === PageStatus.LOADING}
            />

            {templateId !== null ? (
              <TemplateMessage
                orgId={org_id}
                templateId={templateId}
                recipients={listedRecipients}
                sms={sendSMS}
                email={sendEmail}
                wording={templateWording}
                onWording={setTemplateWording}
                disabled={status === PageStatus.LOADING}
              />
            ) : (
              <>
                <label htmlFor="subject" className="input-label">
                  <span className="font-bold">Subject</span>
                  {!sendEmail || formId !== null ? " (Optional)" : ""}
                </label>
                <TextInput
                  id="subject"
                  name="subject"
                  placeholder="Subject"
                  value={newMessage.subject}
                  setValue={handleUpdate}
                  disabled={status === PageStatus.LOADING}
                  classes="rounded-lg"
                  required={sendEmail && formId === null}
                />

                <label htmlFor="content" className="mt-3 input-label font-bold">
                  Content
                  {formId !== null && (
                    <span className="font-normal"> (Optional)</span>
                  )}
                </label>
                <textarea
                  id="content"
                  name="content"
                  className="input bg-gray-100 border-0 mt-0 rounded-lg"
                  placeholder={
                    formId !== null
                      ? "A note before the link (optional)"
                      : "Your message here..."
                  }
                  rows={5}
                  value={newMessage.content}
                  onChange={handleUpdate}
                  disabled={status === PageStatus.LOADING}
                  required={formId === null}
                  maxLength={formId !== null ? 1000 : undefined}
                />
              </>
            )}

            <div
              style={{
                display: "flex",
                gap: "1rem",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            >
              {(!template || template.has_sms) && (
                <label style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={sendSMS}
                    onChange={(e) => setSendSMS(e.target.checked)}
                    style={{
                      appearance: "none",
                      width: "18px",
                      height: "18px",
                      border: "2px solid #4ade80",
                      borderRadius: "4px",
                      backgroundColor: sendSMS ? "#4ade80" : "#fff",
                      cursor: "pointer",
                    }}
                  />
                  <span style={{ marginLeft: 8 }}>SMS</span>
                </label>
              )}

              {(!template || template.has_email) && (
                <label style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    style={{
                      appearance: "none",
                      width: "18px",
                      height: "18px",
                      border: "2px solid #4ade80",
                      borderRadius: "4px",
                      backgroundColor: sendEmail ? "#4ade80" : "#fff",
                      cursor: "pointer",
                    }}
                  />
                  <span style={{ marginLeft: 8 }}>Email</span>
                </label>
              )}
            </div>

            <DialogActions>
              <DialogAction
                primary={true}
                color={disableSendButton ? ButtonColor.GRAY : ButtonColor.GREEN}
                type="submit"
                disabled={disableSendButton}
              >
                {status === PageStatus.LOADING ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : formId !== null ? (
                  <>Send Form</>
                ) : (
                  <>Send Message</>
                )}
              </DialogAction>

              <DialogClose asChild>
                <DialogAction
                  color={ButtonColor.BLACK}
                  disabled={status === PageStatus.LOADING}
                >
                  Discard
                </DialogAction>
              </DialogClose>
            </DialogActions>
          </form>
        </div>
      </DialogContent>
    </>
  );
}

export default ComposeMessageDialog;
